
import { useState } from 'react'
import toast from 'react-hot-toast'

import juice from 'juice'

import { excelLogo, newWordLogo, pdfIcon } from '@assets'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ContentApiCaller } from '@frontend/api'
import { useSentry } from '@utils/frontend'

interface DownloadDocumentProps {
	readonly title: string;
	readonly type: 'artifact' | 'session' | 'block';
	readonly dropdownSide: 'up' | 'down';
	readonly onDownload: (format: 'xlsx' | 'docx' | 'pdf') => void;
	readonly customSize?: string | undefined;
}

const chatMessageStyle = `
.chat-message > *:not(:first-child) {
	margin-top: 1rem;
}

.chat-message > pre {
	width: 100%;
}

.chat-message ul {
	list-style: disc;
	padding-left: 2rem;
}

.chat-message img {
	max-width: 100%;
	height: auto;
	display: block;
	margin: 0.5rem 0;
	border: 1px solid rgb(228 228 231);
	border-radius: 0.5rem;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.chat-message ul ul {
	list-style: circle;
}

.chat-message ul ul ul {
	list-style: square;
}

.chat-message > ul {
	padding-left: 1rem;
}

.chat-message ul li:not(:last-child) {
	margin-bottom: 0.5rem;
}

.chat-message ol {
	list-style: decimal;
	padding-left: 2rem;
}

.chat-message ol ol {
	list-style: lower-alpha;
}

.chat-message ol ol ol {
	list-style: lower-roman;
}

.chat-message table thead {
	border-bottom: 2px solid #1e262e;
}

.chat-message table th, .chat-message table td {
	padding: 0.35rem 0.75rem;
}

.chat-message table td, .chat-message table th {
	border: 1px solid #1e262e;
}

.chat-message table th {
	background-color: rgb(64 64 64);
	color: #fff;
}

.chat-message table tr:nth-child(even) {
	background-color: #f2f2f2;
}

.chat-message > h1 {
	font-weight: 700;
	font-size: 1.6rem;
	margin: 1.2rem 0;
}

.chat-message > h1:first-child {
	margin-top: 0;
}

.chat-message > h2 {
	font-weight: 700;
	font-size: 1.4rem;
	margin: 0.8rem 0;
}

.chat-message > h2:first-child {
	margin-top: 0;
}

.chat-message > h3 {
	font-weight: 700;
	font-size: 1.2rem;
	margin: 0.8rem 0;
}

.chat-message > h3:first-child {
	margin-top: 0;
}
`

export default function DownloadDocument({ title, type, dropdownSide, customSize = 'p-2', onDownload }: DownloadDocumentProps) {
	const [isDownloadDropdownVisible, setIsDownloadDropdownVisible] = useState(false)
	const { captureException } = useSentry()

	function handleDownload(format: 'xlsx' | 'docx' | 'pdf') {
		toast.promise(
			downloadFile(format),
			{
				loading: `Downloading ${format.toUpperCase()}...`,
				success() {
					onDownload(format)

					return `${format.toUpperCase()} downloaded successfully`
				},
				error(error) {
					captureException(error)

					return `Error downloading ${format.toUpperCase()}`
				}
			}
		)
	}

	async function downloadFile(format: 'xlsx' | 'docx' | 'pdf') {
		const chatMessageElement = document.querySelector(`.chat-message.${type}`)

		if (chatMessageElement) {
			const html = `
				<!DOCTYPE html>
				<html>
					<head>
						<style>${chatMessageStyle}</style>
					</head>
					<body>
						${chatMessageElement.outerHTML.replace(/ <span class="vb-source[^>]*>[\s\S]*?<\/span>/gi, '').replace(/width:\s*\d+%;\s*/gi, '')}
					</body>
				</html>`
			const fullHtml = juice(html).replace(/<h(\d)[^>]*>([\s\S]*?)<\/h.>/gi, '<h$1>$2</h$1>')

			const response = await ContentApiCaller.exportFile(fullHtml, format, title ?? 'Exported Document')
			const url = window.URL.createObjectURL(new Blob([response.data]))
			const link = document.createElement('a')

			link.href = url
			link.setAttribute('download', `${title ?? 'Exported Document'}.${format}`)
			document.body.appendChild(link)
			link.click()
			link.remove()
		}
	}

	return (
		<button
			className={ `flex relative items-center justify-center flex-shrink-0 bg-gray-100 border-gray-300 gap-3 text-gray-600 border
	transition-colors duration-300 hover:bg-gray-200 hover:text-gray-500 ${dropdownSide === 'down' ? 'hover:rounded-b-none' : 'hover:rounded-t-none'} rounded-md ${customSize} text-xs min-w-[140px]` }
			onMouseEnter={ () => {
				setIsDownloadDropdownVisible(true)
			} }
			onMouseLeave={ () => {
				setIsDownloadDropdownVisible(false)
			} }
		>
			<FontAwesomeIcon icon="download" className="w-3 h-3" />
			<div className="font-bold">Download file</div>

			{ isDownloadDropdownVisible
				? <div className={ `${dropdownSide === 'down' ? 'top-full rounded-b-md ease-down' : 'bottom-full rounded-t-md ease-up'} absolute bg-white outline outline-1 shadow-md outline-gray-300 flex flex-col z-[60] ${customSize.includes('w-full') ? 'w-full' : ''}` }>
					<a
						className="flex items-center text-gray-600 gap-3 border-b border-gray-300
		transition-colors duration-300 hover:bg-gray-200 p-3 text-xs"
						onClick={ () => {
							handleDownload('xlsx')
						} }
					>
						<img
							src={ excelLogo }
							alt="xlsx"
							className="w-4 h-4" />
						<div>Export to <span className="font-bold">Excel</span></div>
					</a>
					<a
						className="flex items-center text-gray-600 gap-3 border-b border-gray-300
		transition-colors duration-300 hover:bg-gray-200 p-3 text-xs"
						onClick={ () => {
							handleDownload('docx')
						} }
					>
						<img
							src={ newWordLogo }
							alt="docx"
							className="w-4 h-4" />
						<div>Export to <span className="font-bold">Word</span></div>
					</a>
					<a
						className="flex items-center text-gray-600 gap-3
		transition-colors duration-300 hover:bg-gray-200 p-3 text-xs"
						onClick={ () => {
							handleDownload('pdf')
						} }
					>
						<img
							src={ pdfIcon }
							alt="pdf"
							className="w-4 h-4" />
						<div>Export to <span className="font-bold">Pdf</span></div>
					</a>
				</div>
				: null }
		</button>
	)
}
